import React from "react";
import { Image, Platform, Pressable, Text, View } from "react-native";
import Svg, {
  Circle,
  Defs,
  G,
  LinearGradient,
  Mask,
  Path,
  Rect,
  Stop,
  Text as SvgText,
  Use,
} from "react-native-svg";

import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";
import { brandColor } from "../globalStyle";
import { t } from "@/localization";

export const TrashCanButton = ({
  style,
  textStyle,
  iconStyle,
  text,
  icon,
  disabled,
  primary,
  onPress,
  ...extraProps
}) => {
  return (
    <Pressable
      onPress={onPress}
      style={[
        tw`absolute right-[5px] top-2 rounded-[20px] h-[30px] w-[32px] bg-[#AEAEB2] justify-center items-center`,
        style,
      ]}
      {...extraProps}
    >
      <Ionicons name="trash-outline" size={22} color="white" />
    </Pressable>
  );
};

export function GoogleIcon(props) {
  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 -4 60 60"
      {...props}
    >
      <Path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <Path
        fill="#FF3D00"
        d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <Path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <Path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </Svg>
  );
}

export const Profile = (props) => (
  <Svg
    xmlns="http://www.w3.org/2000/svg"
    width={25}
    height={25}
    viewBox="-2 -2 20 20"
    fill="#3F5B7A"
    className="bi bi-person-fill"
    {...props}
  >
    <Path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
  </Svg>
);

export function LiveIndicator(props) {
  return (
    <Svg
      height={35}
      viewBox="10 24 90 90"
      width={50}
      xmlns="http://www.w3.org/2000/svg"
      data-name="Layer 1"
      {...props}
    >
      <Path d="M6.5 44.096h115v39.808H6.5z" fill="#fb4a59" />
      <G fill="#f2f2f2">
        <Path d="M58.131 71.343v3.648H42.868V53.009h3.968v18.334zM61.428 53.009H65.4v21.982h-3.972zM91.186 53.009L82 74.991h-4.445l-9.184-21.982h4.416l6.976 16.671 6.976-16.671zM98.1 56.657v5.312h11.359v3.647H98.1v5.728h12.543v3.648H94.1V53.009h16.543v3.648z" />
        <Circle cx={24.192} cy={64} r={6} />
      </G>
    </Svg>
  );
}

// SVG text on web takes the page font (Times outside a Text); native SVG text
// already uses the system font.
const svgTextFont =
  Platform.OS === "web"
    ? {
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }
    : {};

// LiveIndicator's badge in the brand slate, for a free test that is streaming:
// tests go to the private test link, so they never show the red LIVE.
export function TestIndicator(props) {
  const label = t("TEST");
  // A word longer than "TEST" (Spanish "PRUEBA") gets a smaller size, so it
  // stays inside the badge at the sidebar's 42 px too.
  const fit = label.length > 4;
  return (
    <Svg
      height={35}
      viewBox="10 24 90 90"
      width={50}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <Path d="M6.5 44.096h115v39.808H6.5z" fill="#3f5b7a" />
      <G fill="#f2f2f2">
        <SvgText
          x={fit ? 73 : 76}
          y={fit ? 70 : 73.5}
          fontSize={fit ? 17 : 26}
          fontWeight="bold"
          textAnchor="middle"
          {...svgTextFont}
        >
          {label}
        </SvgText>
        <Circle cx={24.192} cy={64} r={6} />
      </G>
    </Svg>
  );
}

export function UploadVideoIcon(props) {
  return (
    <Svg
      width={53}
      height={46}
      viewBox="0 0 53 46"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      {...props}
    >
      <Defs>
        <LinearGradient
          x1="50%"
          y1="13.073%"
          x2="50%"
          y2="159.157%"
          id="prefix__b"
        >
          <Stop stopColor="#FAFAFA" stopOpacity={0} offset="0%" />
          <Stop stopOpacity={0.062} offset="100%" />
        </LinearGradient>
        <Rect id="prefix__a" x={0} y={0} width={53} height={53} rx={6.9} />
      </Defs>
      <G transform="translate(0 -7)" fill="none" fillRule="evenodd">
        <Mask id="prefix__c" fill="#fff">
          <Use xlinkHref="#prefix__a" />
        </Mask>
        <Use fill="url(#prefix__b)" xlinkHref="#prefix__a" />
        <Path
          d="M26.86 14.376l7.337 7.65a.5.5 0 01-.358.847l-3.154.022a.5.5 0 00-.496.5V33.5a.5.5 0 01-.5.5H23.31a.5.5 0 01-.5-.5V23.399a.5.5 0 00-.496-.5l-3.156-.025a.5.5 0 01-.357-.847l7.337-7.65a.5.5 0 01.722 0z"
          fill="#797A82"
          mask="url(#prefix__c)"
        />
        <Rect
          fill="#929298"
          mask="url(#prefix__c)"
          x={15}
          y={37}
          width={23}
          height={3}
          rx={0.5}
        />
      </G>
    </Svg>
  );
}

export function ChoosePhotoIcon(props) {
  return (
    <Svg
      width={54}
      height={46}
      viewBox="0 0 54 46"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      {...props}
    >
      <Defs>
        <LinearGradient
          x1="50%"
          y1="14.37%"
          x2="50%"
          y2="115.302%"
          id="prefix__b"
        >
          <Stop stopColor="#FAFAFA" stopOpacity={0} offset="0%" />
          <Stop stopOpacity={0.065} offset="100%" />
        </LinearGradient>
        <Rect
          id="prefix__a"
          x={0}
          y={0.345}
          width={53.13}
          height={53.13}
          rx={6.9}
        />
      </Defs>
      <G transform="translate(0 -8)" fill="none" fillRule="evenodd">
        <Mask id="prefix__c" fill="#fff">
          <Use xlinkHref="#prefix__a" />
        </Mask>
        <Use fill="url(#prefix__b)" xlinkHref="#prefix__a" />
        <Circle
          fill="#797A82"
          fillRule="nonzero"
          mask="url(#prefix__c)"
          cx={14.959}
          cy={21.851}
          r={4.14}
        />
        <Path
          d="M48.747 53.724h-23.46l4.546-5.647.327-.407 5.924-7.36c.212-.227.527-.367.867-.384.34-.017.672.09.914.294.03.029.059.06.085.09l10.797 13.414z"
          fillOpacity={0.8}
          fill="#797A82"
          fillRule="nonzero"
          mask="url(#prefix__c)"
        />
        <Path
          d="M40.324 53.724H5.953a.858.858 0 01-.129-.008l16.257-22.98c.202-.292.58-.472.99-.472.411 0 .79.18.991.472l10.91 15.421.523.738 4.83 6.83z"
          fill="#797A82"
          fillRule="nonzero"
          mask="url(#prefix__c)"
        />
      </G>
    </Svg>
  );
}

function BackIcon(props) {
  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width={12}
      height={22}
      viewBox="0 0 12 22"
      {...props}
    >
      <Path
        fill="none"
        stroke="#F84123"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit={20}
        strokeWidth={2}
        d="M10.72 20.44v0L1 10.72v0L10.72 1v0"
      />
    </Svg>
  );
}

export const CalendarIcon = (props) => {
  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={12}
      viewBox="0 0 14 12"
      {...props}
    >
      <Path
        fill="#fff"
        d="M10.85 8h1.1V6.857h-1.1zm0-1.704h1.1V5.143h-1.1zm0-1.725h1.1V3.43h-1.1zm-8.8 5.143h1.1V8.571h-1.1zm2.2 0h1.1V8.571h-1.1zm2.2 0h1.1V8.571h-1.1zM2.05 8h1.1V6.857h-1.1zm2.2 0h1.1V6.857h-1.1zm-2.2-1.704h1.1V5.143h-1.1zm2.2 0h1.1V5.143h-1.1zm2.2 0h1.1V5.143h-1.1zm0-1.725h1.1V3.43h-1.1zM8.65 8h1.1V6.857h-1.1zm0-1.704h1.1V5.143h-1.1zm0-1.725h1.1V3.43h-1.1zM6.45 8h1.1V6.857h-1.1zM.95 3.886a1.6 1.6 0 011.6-1.6h8.9a1.6 1.6 0 011.6 1.6v5.371a1.6 1.6 0 01-1.6 1.6h-8.9a1.6 1.6 0 01-1.6-1.6zM0 10.4A1.6 1.6 0 001.6 12h10.8a1.6 1.6 0 001.6-1.6V1.6A1.6 1.6 0 0012.4 0H1.6A1.6 1.6 0 000 1.6z"
      />
    </Svg>
  );
};

export const CloseIcon = (props) => (
  <Svg
    xmlns="http://www.w3.org/2000/svg"
    width={14}
    height={14}
    viewBox="0 0 14 14"
    {...props}
  >
    <Path
      fill="none"
      stroke="#787878"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit={20}
      strokeWidth={1.5}
      d="M13 1L1 13M1 1l12 12"
    />
  </Svg>
);

export const VideoUploadIcon = (props) => (
  <Svg
    xmlns="http://www.w3.org/2000/svg"
    width={39}
    height={30}
    viewBox="0 0 39 30"
    {...props}
  >
    <Path
      fill="#F84123"
      d="M32.352 25.522h3.989v1.791h-3.989zm-16.84-14.569a.88.88 0 011.33-.755l7.154 4.27a.88.88 0 010 1.511l-7.154 4.271a.88.88 0 01-1.33-.755zm16.84-8.266h3.989v1.79h-3.989zM20.386 25.522h3.979v1.791h-3.979zm0-22.835h3.979v1.79h-3.979zM8.42 25.522h3.99v1.791H8.42zm0-22.835h3.99v1.79H8.42zm18.17 22.835h3.99v1.791h-3.99zm0-22.835h3.99v1.79h-3.99zM14.626 25.522h3.979v1.791h-3.979zm0-22.835h3.979v1.79h-3.979zM2.659 25.522h3.989v1.791H2.659zm0-22.835h3.989v1.79H2.659zM0 27.8A2.2 2.2 0 002.2 30h34.6a2.2 2.2 0 002.2-2.2V2.2A2.2 2.2 0 0036.8 0H2.2A2.2 2.2 0 000 2.2z"
    />
  </Svg>
);

export function AppBackButton({ onPress }) {
  return (
    <Pressable onPress={onPress} style={tw`flex-row items-center`}>
      <BackIcon />
      <Text style={tw`ml-2 text-[${brandColor}]`}>{t("Back")}</Text>
    </Pressable>
  );
}

export function AppSimpleHeaderButton({ text, onPress }) {
  return (
    <Pressable onPress={onPress} style={tw`flex-row items-center`}>
      <Text style={tw`text-[${brandColor}] font-semibold`}>{text}</Text>
    </Pressable>
  );
}

export function GetAppStore({ style, onPress }) {
  return (
    <Pressable style={[tw`opacity-100`, style]} onPress={onPress}>
      <Image
        source={require("@/assets/appstore.png")}
        style={{ width: 128, height: 40 }}
      />
    </Pressable>
  );
}

export function GetPlayStore({ style, onPress }) {
  return (
    <Pressable style={[tw`opacity-100`, style]} onPress={onPress}>
      <Image
        source={require("../../assets/playstore.png")}
        style={{ width: 135, height: 40 }}
      />
    </Pressable>
  );
}

// Same size and look as the store badges above.
export function GetMacApp({ style, onPress }) {
  return (
    <Pressable
      style={[
        tw`h-10 flex-row items-center rounded-md border border-[#a6a6a6] bg-black pl-2 pr-3`,
        style,
      ]}
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={t("Download EventLive for Mac")}
    >
      <Ionicons name="laptop-outline" size={24} color="#fff" />
      <View style={tw`ml-1.5`}>
        <Text style={tw`text-[9px] leading-[11px] text-white`}>
          {t("Download for")}
        </Text>
        <Text style={tw`text-[17px] leading-5 font-semibold text-white`}>
          Mac
        </Text>
      </View>
    </Pressable>
  );
}
